/**
 * The ReAct loop — architecture §6 step 3 (REASON).
 *
 * Reason -> Act -> Observe, bounded to 6 steps, with a tool registry of exactly
 * six tools. The model may only pick from pre-validated options; the finished
 * report is schema-validated with zod, and any failure (bad JSON, unknown tool,
 * step budget exhausted, LLM throws) falls back to the deterministic report.
 *
 * There are no network calls in this file: the loop talks only to `LlmClient`.
 */
import { z } from 'zod';
import type { CoachInput, ExerciseTrendPoint, PersonalRecord } from '../types/coach.js';
import type {
  CoachReport,
  CoachRun,
  EvaluationResult,
  FatigueFlag,
  PlanProposal,
  PrescribedChange,
  ReactStep,
  SafetyRail,
} from '../types/results.js';
import { CoachReportSchema } from '../types/results.js';
import { evaluateBlock, isoWeekStart } from '../evaluation/matrix.js';
import { proposeNextWeek } from '../evaluation/progression.js';
import { applySafetyRails } from '../evaluation/safety-rails.js';
import { round2 } from '../evaluation/math.js';
import type { LlmClient, LlmResponse, LlmStep } from '../adapters/llm.js';
import {
  COACH_SYSTEM_PROMPT,
  EVALUATION_SYSTEM_PROMPT,
  TOOL_DESCRIPTIONS,
  renderUserPrompt,
} from './prompt-system.js';

/** Hard ceiling on ReAct steps; the run falls back to the deterministic report past this. */
export const MAX_REACT_STEPS = 6;
/** One repair attempt is allowed after a schema-invalid `finalize_report`. */
export const MAX_FINALIZE_ATTEMPTS = 2;

export const ReActOptionsSchema = z.object({
  /** Injected clock — never read `Date.now()` in a test. */
  now: z.string().optional(),
  /** Deterministic ids for tests. */
  runId: z.string().optional(),
  maxSteps: z.number().int().min(1).max(MAX_REACT_STEPS).optional(),
  maxTokens: z.number().int().positive().optional(),
  temperature: z.number().min(0).max(2).optional(),
  /** Persist-style labels appended to the deterministic fallback. */
  includeTrace: z.boolean().optional(),
});
export type ReActOptions = z.infer<typeof ReActOptionsSchema>;

// ---------------------------------------------------------------------------
// Tool registry
// ---------------------------------------------------------------------------

const ExerciseTrendInput = z.object({
  exerciseName: z.string().optional(),
  exerciseId: z.string().optional(),
});

const PersonalRecordsInput = z.object({
  exerciseName: z.string().optional(),
  limit: z.number().int().positive().max(50).optional(),
});

const SessionHistoryInput = z.object({
  limit: z.number().int().positive().max(50).optional(),
  label: z.string().optional(),
});

const WorkloadInput = z.object({
  weeks: z.number().int().positive().max(52).optional(),
});

const ProposeChangeInput = z.object({
  exerciseName: z.string().optional(),
});

const FinalizeInput = z.object({}).passthrough();

export interface ToolResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

export interface ToolContext {
  packet: CoachInput;
  evaluation: EvaluationResult;
  proposal: PlanProposal;
  railsHit: SafetyRail[];
  /** Raw `finalize_report` payload, for the loop to schema-validate. */
  setFinalPayload(payload: unknown): void;
}

export interface RegisteredTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  /** zod schema used to validate the model's arguments. */
  argsSchema: z.ZodTypeAny;
  run(input: Record<string, unknown>, context: ToolContext): ToolResult;
}

function matchesName(candidate: string, needle: string | undefined): boolean {
  if (!needle) return true;
  return candidate.toLowerCase().includes(needle.toLowerCase());
}

function trendToJson(points: ReadonlyArray<ExerciseTrendPoint>): unknown {
  return points.map((point) => ({
    weekStart: point.weekStart,
    exerciseName: point.exerciseName,
    topWeightKg: point.topWeightKg,
    bestE1rmKg: point.bestE1rmKg,
    workingSets: point.workingSets,
    failedSets: point.failedSets,
    avgRpe: point.avgRpe,
  }));
}

function prToJson(pr: PersonalRecord): unknown {
  return {
    exerciseName: pr.exerciseName,
    weightKg: pr.weightKg,
    reps: pr.reps,
    repBucket: pr.repBucket,
    estimated1rmKg: pr.estimated1rmKg,
    achievedAt: pr.achievedAt,
  };
}

function changeToJson(change: PrescribedChange): unknown {
  return {
    exerciseName: change.exerciseName,
    dayLabel: change.dayLabel,
    action: change.action,
    rule: change.rule,
    rationale: change.rationale,
    fields: change.fields,
  };
}

function weeklyVolumes(packet: CoachInput): Array<{ weekStart: string; volumeKg: number }> {
  const byWeek = new Map<string, number>();
  for (const session of packet.sessions) {
    const week = isoWeekStart(session.startedAt);
    byWeek.set(week, round2((byWeek.get(week) ?? 0) + session.volumeKg));
  }
  return [...byWeek.entries()]
    .map(([weekStart, volumeKg]) => ({ weekStart, volumeKg }))
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** The six tools the coach is allowed to call. */
export const COACH_TOOLS: ReadonlyArray<RegisteredTool> = [
  {
    name: 'get_exercise_trend',
    description: TOOL_DESCRIPTIONS[0]?.description ?? 'Weekly trend for one exercise.',
    inputSchema: TOOL_DESCRIPTIONS[0]?.inputSchema ?? {},
    argsSchema: ExerciseTrendInput,
    run(input, context) {
      const parsed = ExerciseTrendInput.parse(input);
      const points = context.packet.exerciseTrend.filter(
        (point) =>
          matchesName(point.exerciseName, parsed.exerciseName) &&
          (parsed.exerciseId === undefined || point.exerciseId === parsed.exerciseId),
      );
      if (points.length === 0) {
        return { ok: false, error: `No trend rows matched ${parsed.exerciseName ?? parsed.exerciseId ?? 'the filter'}.` };
      }
      return { ok: true, data: trendToJson(points) };
    },
  },
  {
    name: 'get_personal_records',
    description: TOOL_DESCRIPTIONS[1]?.description ?? 'Personal records in the window.',
    inputSchema: TOOL_DESCRIPTIONS[1]?.inputSchema ?? {},
    argsSchema: PersonalRecordsInput,
    run(input, context) {
      const parsed = PersonalRecordsInput.parse(input);
      const limit = parsed.limit ?? 10;
      const records = context.packet.personalRecords
        .filter((pr) => matchesName(pr.exerciseName, parsed.exerciseName))
        .slice(0, limit);
      if (records.length === 0) {
        return { ok: false, error: 'No personal records matched the filter in this window.' };
      }
      return { ok: true, data: records.map(prToJson) };
    },
  },
  {
    name: 'get_session_history',
    description: TOOL_DESCRIPTIONS[2]?.description ?? 'Completed sessions in the window.',
    inputSchema: TOOL_DESCRIPTIONS[2]?.inputSchema ?? {},
    argsSchema: SessionHistoryInput,
    run(input, context) {
      const parsed = SessionHistoryInput.parse(input);
      const limit = parsed.limit ?? 8;
      const sessions = [...context.packet.sessions]
        .filter((session) => matchesName(session.label ?? '', parsed.label))
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
        .slice(0, limit)
        .map((session) => ({
          label: session.label,
          startedAt: session.startedAt,
          volumeKg: session.volumeKg,
          setsCompleted: session.setsCompleted,
          prCount: session.prCount,
          failedSets: session.failedSets,
          avgRpe: session.avgRpe,
        }));
      if (sessions.length === 0) {
        return { ok: false, error: 'No completed sessions matched the filter in this window.' };
      }
      return { ok: true, data: sessions };
    },
  },
  {
    name: 'compute_workload',
    description: TOOL_DESCRIPTIONS[3]?.description ?? 'Weekly workload and acute:chronic ratio.',
    inputSchema: TOOL_DESCRIPTIONS[3]?.inputSchema ?? {},
    argsSchema: WorkloadInput,
    run(input, context) {
      const parsed = WorkloadInput.parse(input);
      const all = weeklyVolumes(context.packet);
      const weeks = parsed.weeks ? all.slice(-parsed.weeks) : all;
      const volumes = weeks.map((week) => week.volumeKg);
      const chronic = volumes.length > 0 ? volumes.reduce((sum, value) => sum + value, 0) / volumes.length : 0;
      const acute = volumes.length > 0 ? (volumes[volumes.length - 1] ?? 0) : 0;
      return {
        ok: true,
        data: {
          weeks,
          totalVolumeKg: round2(volumes.reduce((sum, value) => sum + value, 0)),
          weekOverWeekPct:
            volumes.length >= 2
              ? round2((((volumes[volumes.length - 1] ?? 0) - (volumes[volumes.length - 2] ?? 0)) / Math.max(volumes[volumes.length - 2] ?? 1, 1)) * 100)
              : 0,
          acuteChronicRatio: chronic > 0 ? round2(acute / chronic) : 0,
          evaluationRatio: context.evaluation.metrics.acuteChronicRatio,
          fatigueFlags: context.evaluation.fatigueFlags.map((flag) => flag.code),
        },
      };
    },
  },
  {
    name: 'propose_exercise_change',
    description: TOOL_DESCRIPTIONS[4]?.description ?? 'Validated double-progression changes.',
    inputSchema: TOOL_DESCRIPTIONS[4]?.inputSchema ?? {},
    argsSchema: ProposeChangeInput,
    run(input, context) {
      const parsed = ProposeChangeInput.parse(input);
      const changes = context.proposal.changes.filter((change) =>
        matchesName(change.exerciseName, parsed.exerciseName),
      );
      return {
        ok: true,
        data: {
          basedOnVerdict: context.proposal.basedOnVerdict,
          week: context.proposal.week,
          needsMoreData: context.proposal.needsMoreData,
          notes: context.proposal.notes,
          changes: changes.map(changeToJson),
          safetyRails: context.railsHit.map((rail) => ({ code: rail.code, explanation: rail.explanation })),
        },
      };
    },
  },
  {
    name: 'finalize_report',
    description: TOOL_DESCRIPTIONS[5]?.description ?? 'Emit the finished report JSON.',
    inputSchema: TOOL_DESCRIPTIONS[5]?.inputSchema ?? {},
    argsSchema: FinalizeInput,
    run(input, context) {
      context.setFinalPayload(input);
      return { ok: true, data: { accepted: true } };
    },
  },
];

export function toolByName(name: string): RegisteredTool | undefined {
  return COACH_TOOLS.find((tool) => tool.name === name);
}

// ---------------------------------------------------------------------------
// Deterministic report (fallback + reference for the LLM)
// ---------------------------------------------------------------------------

function fatigueChecklist(flags: ReadonlyArray<FatigueFlag>): string[] {
  const items: string[] = [];
  for (const flag of flags) {
    switch (flag.code) {
      case 'weekly_avg_rpe_high':
        items.push('Cap the top set at RPE 8 next week, even if that means fewer kilos.');
        break;
      case 'rpe_creep':
        items.push('Same loads, lower effort: stop two reps short of the grinder.');
        break;
      case 'failed_set_rate_high':
        items.push('Log every set honestly — a miss is information, not a failure.');
        break;
      case 'volume_spike':
        items.push('Hold this week’s volume flat instead of adding work.');
        break;
      case 'acute_chronic_ratio_high':
        items.push('Spread the volume across the week instead of stacking it.');
        break;
      case 'acute_chronic_ratio_low':
        items.push('You have room: add one working set to the main lift.');
        break;
      case 'compliance_collapse':
        items.push('Book the sessions. Three short sessions beat one perfect one.');
        break;
      default:
        items.push(flag.message);
    }
  }
  return items;
}

function proposalChecklist(changes: ReadonlyArray<PrescribedChange>): string[] {
  const items: string[] = [];
  for (const change of changes) {
    const weight = change.fields.find((field) => field.field === 'targetWeightKg');
    if (change.action === 'increase_load' && weight) {
      items.push(`${change.exerciseName}: ${weight.before ?? 0} → ${weight.after ?? 0} kg, same reps.`);
    } else if (change.action === 'hold_and_add_rep') {
      const repMax = change.fields.find((field) => field.field === 'repMax');
      items.push(`${change.exerciseName}: hold the load, take the top set to ${repMax?.after ?? 'one more'} reps.`);
    } else if (change.action === 'reduce_load' && weight) {
      items.push(`${change.exerciseName}: take 5% off to ${weight.after ?? 0} kg and rebuild the reps.`);
    } else if (change.action === 'deload' && weight) {
      items.push(`${change.exerciseName}: deload to ${weight.after ?? 0} kg, stop at RPE ${change.fields.find((f) => f.field === 'targetRpe')?.after ?? 7}.`);
    } else if (change.action === 'blocked') {
      items.push(`${change.exerciseName}: substituted — ${change.note ?? 'see the report note'}.`);
    }
  }
  return items;
}

/**
 * Compose the report entirely from deterministic data. Used when the LLM is
 * unavailable, stubborn, or schema-invalid — the coach never ships an empty week.
 */
export function buildDeterministicReport(
  evaluation: EvaluationResult,
  proposal: PlanProposal,
  railsHit: ReadonlyArray<SafetyRail> = [],
): CoachReport {
  const summaryLines: string[] = [evaluation.headline, ''];
  summaryLines.push(
    `**${evaluation.metrics.sessionsAnalyzed} sessions · ${evaluation.metrics.totalVolumeKg.toFixed(0)} kg moved · avg RPE ${evaluation.metrics.avgRpe.toFixed(2)} · ${evaluation.metrics.compliancePct.toFixed(1)}% compliance**`,
    '',
  );
  if (evaluation.verdictReasons.length > 0) {
    summaryLines.push(evaluation.verdictReasons.map((reason) => `- ${reason}`).join('\n'), '');
  }
  if (evaluation.fatigueFlags.length > 0) {
    summaryLines.push('**What the numbers flagged**', '');
    for (const flag of evaluation.fatigueFlags) summaryLines.push(`- ${flag.message}`);
    summaryLines.push('');
  }
  if (evaluation.metrics.prCount > 0) {
    summaryLines.push(`${evaluation.metrics.prCount} PR${evaluation.metrics.prCount === 1 ? '' : 's'} logged this block. That is the work showing up.`, '');
  }
  if (proposal.notes.length > 0) {
    summaryLines.push(proposal.notes.join(' '), '');
  }
  if (railsHit.length > 0) {
    summaryLines.push(`Safety layer: ${railsHit.map((rail) => rail.code).join(', ')}.`, '');
  }

  const checklist = [...proposalChecklist(proposal.changes), ...fatigueChecklist(evaluation.fatigueFlags)];
  if (checklist.length === 0) {
    checklist.push('Run the plan as written and log every working set.');
  }

  const exerciseNotes = evaluation.exerciseInsights.slice(0, 6).map((insight) => ({
    exerciseName: insight.exerciseName,
    note: insight.reason,
  }));

  return {
    verdict: evaluation.verdict,
    headline: evaluation.headline,
    summaryMd: summaryLines.join('\n').trim(),
    nextWeekChecklist: checklist.slice(0, 5),
    exerciseNotes,
  };
}

// ---------------------------------------------------------------------------
// The loop
// ---------------------------------------------------------------------------

const SYSTEM_PROMPT = `${COACH_SYSTEM_PROMPT}\n\n${EVALUATION_SYSTEM_PROMPT}`;

function defaultRunId(packet: CoachInput, startedAt: string): string {
  return `run-${packet.userId.slice(0, 8)}-${startedAt.replace(/[^0-9]/g, '').slice(0, 14)}`;
}
/**
 * Run the bounded ReAct loop.
 *
 * Guarantees:
 *  - at most `opts.maxSteps` (default 6) LLM turns;
 *  - every step is recorded as a `ReactStep` for `agent_coaching_logs`;
 *  - `finalize_report` output is validated with `CoachReportSchema`; one repair
 *    attempt is made, then the deterministic report is used;
 *  - an LLM error or an exhausted budget also falls back, so this never throws
 *    for transport reasons.
 */
export async function runCoachReAct(
  packet: CoachInput,
  llm: LlmClient,
  opts: ReActOptions = {},
): Promise<CoachRun> {
  const parsedOpts = ReActOptionsSchema.parse(opts);
  const startedAt = parsedOpts.now ?? packet.generatedAt;
  const runId = parsedOpts.runId ?? defaultRunId(packet, startedAt);
  const maxSteps = parsedOpts.maxSteps ?? MAX_REACT_STEPS;

  const evaluation = evaluateBlock(packet, { now: startedAt });
  const rawProposal = proposeNextWeek(packet, evaluation);
  const railOutcome = applySafetyRails(rawProposal, evaluation, packet.profile, packet.activePlan);
  const proposal = railOutcome.proposal;
  const railsHit = railOutcome.railsHit;

  const steps: ReactStep[] = [];
  const transcript: LlmStep[] = [];
  let tokensIn = 0;
  let tokensOut = 0;
  let latencyMs = 0;
  let model = llm.model;
  let finalPayload: unknown = null;
  let finalizeAttempts = 0;
  let finalReport: CoachReport | null = null;
  let fallbackReason: string | undefined;

  const baseUserPrompt = renderUserPrompt(packet, evaluation);

  for (let index = 0; index < maxSteps; index += 1) {
    if (finalReport) break;

    const repairNote =
      finalizeAttempts > 0
        ? `\n\nREPAIR REQUIRED\nYour previous finalize_report payload failed schema validation (${fallbackReason ?? 'invalid payload'}). Return the same content with every required field present: verdict (enum), headline (non-empty string), summaryMd (non-empty string), nextWeekChecklist (1+ strings).`
        : '';
    const userPrompt =
      finalizeAttempts > 0
        ? `${baseUserPrompt}${repairNote}`
        : `${baseUserPrompt}\n\nSo far you have called: ${transcript.map((step) => step.action).join(', ') || 'nothing'}. Continue the loop — call exactly one tool.`;

    let response: LlmResponse;
    try {
      response = await llm.complete({
        system: SYSTEM_PROMPT,
        user: userPrompt,
        // Snapshot the transcript: the adapter stores the request, and a live
        // reference would let later steps mutate history the model already saw.
        tools: TOOL_DESCRIPTIONS.map((tool) => ({
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
        })),
        steps: transcript.map((step) => ({ ...step })),
        ...(parsedOpts.maxTokens !== undefined ? { maxTokens: parsedOpts.maxTokens } : {}),
        ...(parsedOpts.temperature !== undefined ? { temperature: parsedOpts.temperature } : {}),
      });
    } catch (error) {
      fallbackReason = `LLM call failed: ${error instanceof Error ? error.message : String(error)}`;
      steps.push({
        index,
        thought: 'Model call failed; falling back to the deterministic report.',
        action: 'finalize_report',
        actionInput: {},
        observation: { fallback: true },
        tokensIn: 0,
        tokensOut: 0,
        latencyMs: 0,
        error: fallbackReason,
      });
      break;
    }

    tokensIn += response.usage.tokensIn;
    tokensOut += response.usage.tokensOut;
    latencyMs += response.latencyMs;
    model = response.model || llm.model;

    const call = response.toolCalls[0];
    const tool = call ? toolByName(call.name) : undefined;
    const thought = response.content.trim();

    if (!call || !tool) {
      const observation = {
        error: call
          ? `Unknown tool "${call.name}". Available: ${COACH_TOOLS.map((entry) => entry.name).join(', ')}.`
          : 'No tool call returned. Respond with exactly one tool call.',
      };
      steps.push({
        index,
        thought: thought || 'No usable action from the model.',
        action: call?.name ?? 'none',
        actionInput: call?.arguments ?? {},
        observation,
        tokensIn: response.usage.tokensIn,
        tokensOut: response.usage.tokensOut,
        latencyMs: response.latencyMs,
      });
      transcript.push({
        index,
        thought: thought || 'No usable action from the model.',
        action: call?.name ?? 'none',
        actionInput: call?.arguments ?? {},
        observation,
      });
      continue;
    }

    const parsedArgs = tool.argsSchema.safeParse(call.arguments);
    if (!parsedArgs.success) {
      const observation = {
        error: `Invalid arguments for ${tool.name}: ${parsedArgs.error.issues.map((issue) => `${issue.path.join('.') || '(root)'} ${issue.message}`).join('; ')}`,
      };
      steps.push({
        index,
        thought: thought || `Attempted ${tool.name}.`,
        action: tool.name,
        actionInput: call.arguments,
        observation,
        tokensIn: response.usage.tokensIn,
        tokensOut: response.usage.tokensOut,
        latencyMs: response.latencyMs,
      });
      transcript.push({
        index,
        thought: thought || `Attempted ${tool.name}.`,
        action: tool.name,
        actionInput: call.arguments,
        observation,
      });
      continue;
    }

    const context: ToolContext = {
      packet,
      evaluation,
      proposal,
      railsHit,
      setFinalPayload: (payload) => {
        finalPayload = payload;
      },
    };

    let result: ToolResult;
    try {
      result = tool.run(parsedArgs.data as Record<string, unknown>, context);
    } catch (error) {
      result = { ok: false, error: error instanceof Error ? error.message : String(error) };
    }

    let accepted = false;
    let stepError: string | undefined;

    if (tool.name === 'finalize_report') {
      finalizeAttempts += 1;
      const validated = CoachReportSchema.safeParse(finalPayload);
      if (validated.success) {
        finalReport = validated.data;
        accepted = true;
        result = { ok: true, data: { accepted: true } };
      } else {
        const issues = validated.error.issues
          .map((issue) => `${issue.path.join('.') || '(root)'} ${issue.message}`)
          .join('; ');
        result = { ok: false, error: `finalize_report failed validation: ${issues}` };
        stepError = result.error;
        fallbackReason = result.error;
        if (finalizeAttempts >= MAX_FINALIZE_ATTEMPTS) {
          fallbackReason = `finalize_report failed validation twice: ${issues}`;
        }
      }
    }

    const step: ReactStep = {
      index,
      thought: thought || `Calling ${tool.name}.`,
      action: tool.name,
      actionInput: parsedArgs.data as Record<string, unknown>,
      observation: result.ok ? result.data : { error: result.error },
      tokensIn: response.usage.tokensIn,
      tokensOut: response.usage.tokensOut,
      latencyMs: response.latencyMs,
      ...(accepted ? { final: true } : {}),
      ...(stepError ? { error: stepError } : {}),
    };
    steps.push(step);
    transcript.push({
      index,
      thought: step.thought,
      action: step.action,
      actionInput: step.actionInput,
      observation: step.observation,
    });

    if (finalReport) break;
    if (finalizeAttempts >= MAX_FINALIZE_ATTEMPTS) break;
  }

  let fallbackUsed = false;
  let report = finalReport;
  if (!report) {
    fallbackUsed = true;
    report = buildDeterministicReport(evaluation, proposal, railsHit);
    if (!fallbackReason) {
      if (finalizeAttempts > 0) {
        fallbackReason = `finalize_report failed validation (${finalizeAttempts} attempt(s)); using the deterministic report.`;
      } else {
        fallbackReason = `Step budget of ${maxSteps} exhausted before finalize_report; using the deterministic report.`;
      }
    }
  }

  const result: CoachRun = {
    runId,
    userId: packet.userId,
    startedAt,
    steps,
    evaluation,
    proposal,
    railsHit,
    report,
    fallbackUsed,
    model,
    tokensIn,
    tokensOut,
    latencyMs,
  };
  if (fallbackReason) result.fallbackReason = fallbackReason;
  return result;
}
